import { useEffect, useRef, useState, type ElementType, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

type RevealVariant = 'up' | 'slide-l' | 'slide-r' | 'scale';

interface RevealProps {
  children: ReactNode;
  className?: string;
  /** Direction of the entrance animation. */
  variant?: RevealVariant;
  /** Delay in milliseconds before the reveal starts. */
  delay?: number;
  as?: ElementType;
}

/**
 * Scroll reveal wrapper from the Artesã Design System.
 * Fades, lifts and unblurs its content once it enters the viewport.
 * Automatically inert when the user prefers reduced motion (handled in CSS).
 */
const Reveal = ({ children, className, variant = 'up', delay = 0, as: Tag = 'div' }: RevealProps) => {
  const ref = useRef<HTMLElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <Tag
      ref={ref}
      style={delay ? { transitionDelay: `${delay}ms` } : undefined}
      className={cn(
        'reveal',
        variant === 'slide-l' && 'slide-l',
        variant === 'slide-r' && 'slide-r',
        variant === 'scale' && 'scale',
        visible && 'is-visible',
        className,
      )}
    >
      {children}
    </Tag>
  );
};

export default Reveal;
